import React from "react";
import { ShoppingBasket, HeartHandshake, Sprout } from "lucide-react";
import { Reveal, WfButton } from "@/components/site/primitives";
import { scrollToId } from "@/lib/scrollTo";

const callouts = [
  { id: "where-to-buy", icon: ShoppingBasket, title: "Where to buy crops", text: "Find fresh, culturally relevant produce near you." },
  { id: "support", icon: HeartHandshake, title: "I want to support", text: "Give or volunteer to power our farmers’ success." },
  { id: "who-we-are", icon: Sprout, title: "Who we are", text: "Discover four decades of growing community." },
];

export const FinalCTA = () => {
  const go = (id) => (e) => { e.preventDefault(); scrollToId(id); };
  return (
    <section id="get-involved" aria-labelledby="final-cta-heading" className="wf-grain relative overflow-hidden bg-[var(--wf-maroon)] py-16 sm:py-20 lg:py-28">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 bg-[radial-gradient(900px_500px_at_85%_10%,rgba(255,253,248,0.10),transparent_60%)]" />
      <div className="wf-container relative">
        <Reveal className="max-w-3xl">
          <p className="inline-flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.18em] text-[rgba(255,253,248,0.8)]">
            <span aria-hidden="true" className="h-px w-6 bg-[rgba(255,253,248,0.6)]" />
            Join the community
          </p>
          <h2 id="final-cta-heading" className="mt-4 font-serif text-3xl font-semibold leading-tight text-[var(--wf-paper)] sm:text-4xl lg:text-5xl">
            Every farmer’s success starts with a community that shows up.
          </h2>
          <p className="mt-4 max-w-[56ch] text-base leading-7 text-[rgba(255,253,248,0.86)]">
            Shop the harvest, give what you can, or get to know the people growing it. There’s a place for you at World Farmers.
          </p>
        </Reveal>

        <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-3">
          {callouts.map((c, idx) => {
            const Icon = c.icon;
            return (
              <Reveal key={c.id} delay={0.07 * idx}>
                <button
                  type="button"
                  onClick={() => scrollToId(c.id === "support" ? "get-involved" : c.id)}
                  data-testid={`final-cta-callout-${c.id}`}
                  className="group flex h-full w-full flex-col items-start gap-3 rounded-sm border border-[rgba(255,253,248,0.18)] bg-[rgba(255,253,248,0.06)] p-6 text-left backdrop-blur-sm transition-colors duration-300 hover:bg-[rgba(255,253,248,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--wf-paper)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--wf-maroon)]"
                >
                  <span className="inline-flex h-11 w-11 items-center justify-center rounded-sm bg-[rgba(255,253,248,0.14)] text-[var(--wf-paper)]">
                    <Icon className="h-5 w-5" aria-hidden="true" />
                  </span>
                  <span className="font-serif text-xl font-semibold text-[var(--wf-paper)]">{c.title}</span>
                  <span className="text-sm leading-6 text-[rgba(255,253,248,0.82)]">{c.text}</span>
                </button>
              </Reveal>
            );
          })}
        </div>

        <Reveal delay={0.1}>
          <div className="mt-10 flex flex-wrap items-center gap-4">
            <WfButton as="a" href="#get-involved" onClick={go("get-involved")} variant="light" withArrow className="text-base" data-testid="final-cta-get-involved-button">
              Get Involved
            </WfButton>
            <a
              href="#where-to-buy"
              onClick={go("where-to-buy")}
              className="text-sm font-semibold text-[var(--wf-paper)] underline decoration-[rgba(255,253,248,0.5)] underline-offset-4 hover:decoration-[var(--wf-paper)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--wf-paper)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--wf-maroon)] rounded-sm"
            >
              Or explore where to buy crops
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
